import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { XIcon } from 'lucide-react';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = isMac ? '⌘' : 'Ctrl';

const SHORTCUTS: { label: string; keys: string[][]; editorOnly?: boolean }[] = [
  { label: 'Search', keys: [[MOD, 'K'], ['/']] },
  { label: 'Full screen preview', keys: [[MOD, 'P']], editorOnly: true },
  { label: 'Exit full screen', keys: [['Esc']], editorOnly: true },
  { label: 'Show shortcuts', keys: [['?']] },
];

const EDITOR_PATH = /^\/pages\/[^/]+\/customize(\/|$)/;

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded bg-white/10 px-1.5 font-sans text-[11px] font-medium text-white">
      {children}
    </kbd>
  );
}

/** Pressing ? anywhere outside a text field lists the dashboard's keyboard shortcuts. */
export function KeyboardShortcuts() {
  const [isOpen, setOpen] = useState(false);
  const isEditor = EDITOR_PATH.test(useLocation().pathname);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (
        event.key === '?' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isEditableTarget(event.target)
      ) {
        event.preventDefault();
        setOpen(open => !open);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-label="Keyboard shortcuts"
      className="fixed bottom-4 left-4 z-[60] w-72 rounded-xl bg-gray-900 dark:bg-popover dark:border dark:border-border p-4 text-white dark:text-popover-foreground shadow-2xl animate-in fade-in-0 slide-in-from-bottom-2"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Keyboard shortcuts</h2>
        <button
          type="button"
          aria-label="Close"
          onClick={() => setOpen(false)}
          className="-mr-1 inline-flex size-6 items-center justify-center rounded text-white/60 transition-colors hover:bg-white/10 hover:text-white"
        >
          <XIcon className="size-4" />
        </button>
      </div>
      <ul className="space-y-2.5">
        {SHORTCUTS.filter(({ editorOnly }) => isEditor || !editorOnly).map(({ label, keys }) => (
          <li key={label} className="flex items-center justify-between gap-4 text-[13px]">
            <span className="text-white/80">{label}</span>
            <span className="flex items-center gap-1.5">
              {keys.map((combo, i) => (
                <React.Fragment key={combo.join('+')}>
                  {i > 0 && <span className="text-[11px] text-white/40">or</span>}
                  <span className="flex items-center gap-0.5">
                    {combo.map(key => (
                      <Key key={key}>{key}</Key>
                    ))}
                  </span>
                </React.Fragment>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default KeyboardShortcuts;
